import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Chip from '@material-ui/core/Chip';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Autocomplete, { highlightSubstring } from '../Autocomplete';
import { vat as vatApi } from '@afosto/catalog/api/Vat';
import translations from './translations';

const VatRateSearch = ({ countryCode, ...props }) => {
  const intl = useIntl();
  const [vatRateSuggestions, setVatRateSuggestions] = useState([]);
  const [isFetchingVatRates, setIsFetchingVatRates] = useState(true);

  const renderVatRateOption = (option, { inputValue = '' } = {}) => {
    let label = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      label = option.label;
    }

    return (
      <ListItem component="div" dense disableGutters>
        <ListItemText
          primary={
            <>
              {highlightSubstring(label, inputValue)}{' '}
              {option?.is_default && (
                <Chip
                  label={intl.formatMessage(translations.label.default)}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
            </>
          }
          secondary={option?.administrative_area ? `, ${option?.administrative_area}` : ''}
        />
      </ListItem>
    );
  };

  useEffect(() => {
    let isMounted = true;

    const fetchAllVatRates = async () => {
      setIsFetchingVatRates(true);

      try {
        const response = await vatApi.getVatList({ country: countryCode });
        const { data: vatRates = [] } = response;

        if (isMounted) {
          setVatRateSuggestions(
            vatRates.map(vatRate => ({
              ...vatRate,
              label: `${vatRate.rate}%`,
              value: vatRate.reference,
            })),
          );
        }
      } catch (err) {
        if (isMounted) {
          setVatRateSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingVatRates(false);
        }
      }
    };

    fetchAllVatRates();

    return () => {
      isMounted = false;
    };
  }, [countryCode]);

  return (
    <Autocomplete
      {...props}
      options={vatRateSuggestions}
      loading={isFetchingVatRates}
      autocomplete="off"
      renderOption={renderVatRateOption}
    />
  );
};

VatRateSearch.propTypes = {
  ...Autocomplete.propTypes,
  countryCode: PropTypes.string,
};

VatRateSearch.defaultProps = {
  ...Autocomplete.defaultProps,
  countryCode: undefined,
};

export default VatRateSearch;
